import { createContext, useCallback, useContext, useEffect, useState } from "react";
import client from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "salnes_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    client
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Listen for session-expired events emitted by the axios 401 interceptor.
  // Clearing `user` here forces <Navigate> guards to send the browser to /login
  // BEFORE the persistent flag is consumed, so the banner has a chance to render.
  useEffect(() => {
    const handler = () => {
      sessionStorage.removeItem(TOKEN_KEY);
      setUser(null);
    };
    window.addEventListener("salnes:session-expired", handler);
    return () => window.removeEventListener("salnes:session-expired", handler);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    sessionStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.post("/auth/logout");
    } catch {
      /* ignore */
    }
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
